import type { Ref } from 'vue';

import { useQuery } from '@tanstack/vue-query';

import type { MicroInverters } from '~/types/equipment';

import { useCrudCreate, useCrudUpdate } from '~/shared/hooks/useCrudMutations';
import useCrudQuery from '~/shared/hooks/useCrudQuery';
import { resolveEquipmentConflictMessage } from '~/utils/equipmentErrors';

import microInvertersService from '../services/microInvertersService';

export function useCreateMicroInverter() {
  const handleError = (error: any) => {
    const conflictMessage = resolveEquipmentConflictMessage(error);
    if (conflictMessage) return conflictMessage;
    return error?.response?.data?.message || 'Erro ao cadastrar micro-inversor';
  };

  return useCrudCreate(
    'microInverters',
    'Micro-inversor',
    microInvertersService.create.bind(microInvertersService),
    handleError,
  );
}

export function useMicroInverterById(id: Ref<null | number>) {
  return useQuery({
    enabled: () => !!id.value,
    queryFn: () => {
      if (!id.value) throw new Error('ID é obrigatório');
      return microInvertersService.getById(id.value);
    },
    queryKey: ['microInverter', id],
  });
}

export function useMicroInverters(params: Ref<MicroInverters.GetMicroInvertersRequest>) {
  return useCrudQuery(params, 'microInverters', microInvertersService.list.bind(microInvertersService));
}

export function useUpdateMicroInverter() {
  const handleError = (error: any) => {
    if (error?.response?.status === 404) {
      return 'Micro-inversor não encontrado';
    }
    const conflictMessage = resolveEquipmentConflictMessage(error);
    if (conflictMessage) return conflictMessage;
    return error?.response?.data?.message || 'Erro ao atualizar micro-inversor';
  };

  return useCrudUpdate(
    'microInverters',
    'Micro-inversor',
    microInvertersService.update.bind(microInvertersService),
    handleError,
  );
}
